import { updateModelWithOptions, upsertInInfiniteDataList } from "../../../utils";
import { extractRequestFromQueryKey } from "../../../keys";
import { TellescopeModelFromName } from "../../helper-types";
import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";

export const CALENDAR_EVENTS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"calendar_events"> =
  {
    updateOne: (mutationRequest, mutationResponse) => ({
      download_ics_file: mutationResponse
        ? {
            exact: false,
            request: {
              calendarEventId: mutationResponse?.id ?? mutationRequest.id,
            },
          }
        : undefined, // Don't invalidate on mutate
      session_for_start_link: undefined, // Irrelevant to updateOne changes
      get_status_report: undefined, // Irrelevant to updateOne changes
      load_events: {
        exact: false,
        predicate: ({ state: { data }, queryKey }) => {
          const request = extractRequestFromQueryKey(queryKey);
          if (!request) return false;

          const hasEvent = data?.pages.some((page) =>
            page.events.some((event) => event.id === (mutationResponse?.id ?? mutationRequest.id))
          );
          if (hasEvent) return true;

          if (request.external !== undefined && !!mutationRequest.externalId === request.external) {
            return true;
          }
          if (
            request.userIds?.length &&
            mutationRequest.attendees !== undefined &&
            mutationRequest.attendees.some(
              (attendee) => attendee.type === "user" && request.userIds?.includes(attendee.id)
            )
          ) {
            return true;
          }

          const newStartDate = mutationRequest.startTimeInMS
            ? new Date(mutationRequest.startTimeInMS).getTime()
            : undefined;
          const fromStartTime = request.from ? new Date(request.from).getTime() : 0;
          const toStartTime = request.to ? new Date(request.to).getTime() : Infinity;
          if (!newStartDate) return false;

          return newStartDate <= toStartTime && newStartDate >= fromStartTime;
        },
        updater: (oldData, context) => {
          const queryKey = context?.queryKey;
          if (!oldData || !queryKey) return oldData;
          const request = extractRequestFromQueryKey(queryKey);
          if (!request) return oldData;

          const existingEvent = oldData.pages.reduce<TellescopeModelFromName<"calendar_events"> | undefined>(
            (acc, curr) => {
              if (acc) return acc;
              const matchingEvent = curr.events.find(
                (event) => event.id === (mutationResponse?.id ?? mutationRequest.id)
              );
              return matchingEvent;
            },
            undefined
          );

          const modelMatchesFilter = (event: TellescopeModelFromName<"calendar_events">): boolean => {
            if (request.external === true && !event.externalId) return false;
            if (request.external === false && !!event.externalId) return false;
            if (
              request.userIds?.length &&
              !event.attendees.some((attendee) => attendee.type === "user" && request.userIds?.includes(attendee.id))
            )
              return false;
            const toStartTime = request.to ? new Date(request.to).getTime() : Infinity;
            const fromStartTime = request.from ? new Date(request.from).getTime() : 0;
            const eventStartTime = new Date(event.startTimeInMS).getTime();

            return eventStartTime <= toStartTime && eventStartTime >= fromStartTime;
          };

          const shouldFilter = mutationResponse
            ? !modelMatchesFilter(mutationResponse)
            : existingEvent
            ? !modelMatchesFilter({
                ...existingEvent,
                ...mutationRequest,
              })
            : false;

          if (shouldFilter) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                events: page.events.filter((event) => event.id !== (mutationResponse?.id ?? mutationRequest.id)),
              })),
            };
          }

          const mappedInfiniteData = {
            ...oldData,
            pages: oldData.pages.map((page) => page.events),
          };

          const { _options, ...updates } = mutationRequest;
          const updatedData = upsertInInfiniteDataList({
            infiniteData: mappedInfiniteData,
            matchFn: (event) => event.id === (mutationResponse?.id ?? mutationRequest.id),
            updater: (event) =>
              mutationResponse ??
              (event ? updateModelWithOptions(event, updates, _options) : mutationResponse ?? event),
          });

          return {
            pageParams: updatedData?.pageParams ?? [],
            pages: updatedData?.pages.map((page) => ({ events: page })) ?? [],
          };
        },
      },
      get_events_for_user: {
        exact: false,
        predicate: ({ state: { data }, queryKey }) => {
          const existingEvent = data?.pages.reduce<TellescopeModelFromName<"calendar_events"> | undefined>(
            (acc, curr) => {
              if (acc) return acc;
              const matchingEvent = curr.events.find((event) => event.id === mutationRequest.id);
              return matchingEvent;
            },
            undefined
          );

          if (existingEvent) return true;

          const request = extractRequestFromQueryKey(queryKey);

          if (mutationResponse && request) {
            if (
              !mutationResponse.attendees.some((attendee) => attendee.type === "user" && attendee.id === request.userId)
            )
              return false;
            if (
              request.userIds?.length &&
              !mutationResponse.attendees.some(
                (attendee) => attendee.type === "user" && request.userIds?.includes(attendee.id)
              )
            )
              return false;

            const eventStart = new Date(mutationResponse.startTimeInMS).getTime();
            const fromStartTime = request.from ? new Date(request.from).getTime() : 0;
            if (eventStart < fromStartTime) return false;

            const toStartTime = request.to ? new Date(request.to).getTime() : undefined;
            if (toStartTime && eventStart > toStartTime) return false;
          }

          return false;
        },
        updater: (oldData, context) => {
          const queryKey = context?.queryKey;
          const request = queryKey ? extractRequestFromQueryKey(queryKey) : undefined;
          if (!oldData || !request) {
            return oldData;
          }

          const existingEvent = oldData.pages.reduce<TellescopeModelFromName<"calendar_events"> | undefined>(
            (acc, curr) => {
              if (acc) return acc;
              const matchingEvent = curr.events.find((event) => event.id === mutationRequest.id);
              return matchingEvent;
            },
            undefined
          );

          const { _options, ...updates } = mutationRequest;
          const updatedEvent =
            mutationResponse ?? (existingEvent ? updateModelWithOptions(existingEvent, updates, _options) : undefined);

          if (!updatedEvent) return oldData;

          let shouldFilter = false;
          if (existingEvent) {
            if (!updatedEvent.attendees?.some((attendee) => attendee.type === "user" && attendee.id === request.userId))
              shouldFilter = true;
            if (
              request.userIds?.length &&
              !updatedEvent.attendees?.some(
                (attendee) => attendee.type === "user" && request.userIds?.includes(attendee.id)
              )
            )
              shouldFilter = true;

            const eventStart = updatedEvent.startTimeInMS ? new Date(updatedEvent.startTimeInMS).getTime() : undefined;
            const fromStartTime = request.from ? new Date(request.from).getTime() : 0;
            if (eventStart && eventStart < fromStartTime) shouldFilter = true;

            const toStartTime = request.to ? new Date(request.to).getTime() : undefined;
            if (toStartTime && eventStart && eventStart > toStartTime) shouldFilter = true;
          }

          if (shouldFilter) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                events: page.events.filter((event) => event.id === mutationRequest.id),
              })),
            };
          }

          const mappedData = {
            ...oldData,
            pages: oldData.pages.map((page) => page.events),
          };

          const updatedData = upsertInInfiniteDataList({
            infiniteData: mappedData,
            matchFn: (event) => event.id === mutationRequest.id,
            updater: (event) => mutationResponse ?? (event ? updateModelWithOptions(event, updates, _options) : event),
          });

          return {
            pageParams: updatedData?.pageParams ?? [],
            pages:
              updatedData?.pages.map((page) => ({
                events: page,
              })) ?? [],
          };
        },
      },
      get_report: undefined, // Irrelevant to updateOne changes
      get_appointment_availability: mutationResponse
        ? {
            exact: false,
            predicate: ({ queryKey }) => {
              const request = extractRequestFromQueryKey(queryKey);

              if (!request) return false;

              const eventUserIds = mutationResponse.attendees.reduce<string[]>((acc, curr) => {
                if (curr.type === "user") {
                  acc.push(curr.id);
                }
                return acc;
              }, []);

              if (request.userId && !eventUserIds.includes(request.userId)) return false;
              if (request.userIds && !request.userIds.some((userId) => eventUserIds.includes(userId))) return false;
              return true;
            }, // No updater, just invalidate
          }
        : undefined, // No optimistic updates
      get_enduser_report: undefined, // Irrelevant to updateOne changes
    }),
  };
